import * as React from 'react';
import { useObservable } from 'react-use-observable';

import { Col, Row } from 'antd';

import { Field, useFormikContext } from 'formik';
import { SelectFormField } from 'v2/components/Shared/FormFields/SelectFormField';
import { Space } from 'v2/components/Shared/Space';
import useBoolean from 'v2/hooks/useBoolean';
import eduzzAccountsService from 'v2/services/eduzzAccount';

import { FileInput } from '../FileInput';
import { PersonalDocumentType, type NewBankAccount } from '../form';
import { Layout } from '../Layout';
import { Modal } from '../Modal';

const documentTypeOptions = [
  { value: PersonalDocumentType.RG, label: 'RG' },
  { value: PersonalDocumentType.CNH, label: 'CNH' },
  { value: PersonalDocumentType.PROFESSIONAL, label: 'Identidade Profissional' }
];

function Documents() {
  const { values } = useFormikContext<NewBankAccount>();
  const [isRgModalOpen, setIsRgModalState] = useBoolean(false);
  const [isSelfieModalOpen, setIsSelfieModalState] = useBoolean(false);
  const [isCnhModalOpen, setIsCnhModalState] = useBoolean(false);
  const [user] = useObservable(() => eduzzAccountsService.getUser(), []);

  const isCrossborder = user?.international_account || user?.crossborder;

  function getHelperText(value: string) {
    let document = 'Seu documento';
    if (value === PersonalDocumentType.RG) document = 'Seu RG';
    if (value === PersonalDocumentType.CNH) document = 'Sua CNH';
    if (value === PersonalDocumentType.PROFESSIONAL) document = 'Sua Identidade profissional';

    return `${document} precisa conter o número do seu CPF;`;
  }

  if (!user) return null;

  return (
    <>
      <Layout
        title='Documentos'
        description='Agora precisamos que você nos envie uma foto de um documento que contenha o número do seu CPF, pode ser seu RG, CNH ou Identidade Profissional'
      >
        <Row gutter={[16, 16]} className='-mx-2 my-6'>
          <Col xs={24} sm={20}>
            <Field
              name='document_image_type'
              label='Selecione o documento pessoal*'
              fullWidth
              options={documentTypeOptions}
              helperText={getHelperText(values.document_image_type)}
              component={SelectFormField}
            />
          </Col>
          {values.document_image_type === PersonalDocumentType.RG && (
            <>
              <Col xs={24} sm={20}>
                <FileInput
                  name='document_image_front'
                  label='Enviar foto do RG - Frente*'
                  validFormats='image/jpeg, image/png, image/jpg, application/pdf'
                  helperText={
                    <span
                      id='rg-front-image-example-btn'
                      className='text-content-subtitle [&_strong]:text-primary cursor-pointer [&_strong]:font-semibold'
                      role='button'
                      onClick={setIsRgModalState.toTrue}
                    >
                      Exemplo da frente RG: <strong>Ver exemplo</strong>
                    </span>
                  }
                />
              </Col>
              <Col xs={24} sm={20}>
                <FileInput
                  name='document_image_back'
                  label='Enviar foto do RG - Verso*'
                  validFormats='image/jpeg, image/png, image/jpg, application/pdf'
                  helperText={
                    <span
                      id='rg-back-image-example-btn'
                      className='text-content-subtitle [&_strong]:text-primary cursor-pointer [&_strong]:font-semibold'
                      role='button'
                      onClick={setIsRgModalState.toTrue}
                    >
                      Exemplo do verso RG: <strong>Ver exemplo</strong>
                    </span>
                  }
                />
              </Col>
            </>
          )}
          {values.document_image_type === PersonalDocumentType.CNH && (
            <Col xs={24} sm={20}>
              {isCrossborder ? (
                <FileInput
                  name='document_image_front'
                  label='Enviar foto do AOI/EIN*'
                  validFormats='image/jpeg, image/png, image/jpg, application/pdf'
                  helperText='Documento de Constituição de Empresa (Article of Incorporation) ou do Employer Identification Number (EIN) expedido pelo IRS'
                />
              ) : (
                <FileInput
                  name='document_image_front'
                  label='Enviar foto da CNH*'
                  validFormats='image/jpeg, image/png, image/jpg, application/pdf'
                  helperText={
                    <span
                      id='cnh-image-example-btn'
                      className='text-content-subtitle [&_strong]:text-primary cursor-pointer [&_strong]:font-semibold'
                      role='button'
                      onClick={setIsCnhModalState.toTrue}
                    >
                      Exemplo da CNH: <strong>Ver exemplo</strong>
                    </span>
                  }
                />
              )}
            </Col>
          )}
          {values.document_image_type === PersonalDocumentType.PROFESSIONAL && (
            <>
              <Col xs={24} sm={20}>
                <FileInput
                  name='document_image_front'
                  label='Enviar foto da Identidade Profissional - Frente*'
                  validFormats='image/jpeg, image/png, image/jpg, application/pdf'
                />
              </Col>
              <Col xs={24} sm={20}>
                <FileInput
                  name='document_image_back'
                  label='Enviar foto da Identidade Profissional - Verso*'
                  validFormats='image/jpeg, image/png, image/jpg, application/pdf'
                />
              </Col>
            </>
          )}
          <Col span={24}>
            <p className='text-primary text-[[0.625rem]'>
              O arquivo deve ter no máximo 2MB e estar no formato .jpg, .jpeg, .png ou .pdf
            </p>
          </Col>
          <Col span={24}>
            <h3 className='text-primary leading-1.2! mb-2! text-2xl! font-semibold'>Agora um vídeo de validação</h3>
            <p className='text-content-subtitle text-base'>
              Falta pouco! Para validar sua identidade precisamos que você nos envie um vídeo segurando seu documento e
              lembre-se em gravar no modo selfie
            </p>
          </Col>
          <Col xs={24} sm={20}>
            <FileInput
              name='selfie_video'
              label='Enviar vídeo*'
              helperText={
                <>
                  <span
                    id='selfie-video-example-btn'
                    className='text-content-subtitle [&_strong]:text-primary cursor-pointer [&_strong]:font-semibold'
                    role='button'
                    onClick={setIsSelfieModalState.toTrue}
                  >
                    Exemplo do vídeo selfie: <strong>Ver exemplo</strong>
                  </span>
                  <Space axis='vertical' size={8} />
                  <span className='text-primary text-[[0.625rem]'>
                    É rapidinho! Um vídeo de 3 a 7 segundos de duração e no máximo 35MB já é o suficiente
                  </span>
                </>
              }
              validFormats='video/avi, video/x-msvideo, video/mp4, video/quicktime, video/x-ms-wmv, video/x-matroska, video/webm'
            />
          </Col>
        </Row>
        <Modal isOpen={isRgModalOpen} handleClose={setIsRgModalState.toFalse}>
          <div className='flex gap-10 max-md:flex-col max-md:items-center max-md:[&_svg]:h-fit max-md:[&_svg]:w-full'>
            <section className='flex flex-col items-center justify-center max-md:[&_p]:mb-0'>
              <p className='text-primary mb-5 flex items-end text-2xl leading-5 font-semibold [&_span]:ml-2 [&_span]:text-xl [&_span]:leading-5'>
                Verso do RG <span>(com CPF)</span>
              </p>
              <img src='/img/back-rg.jpg' alt='Verso do RG com CPF - exemplo' />
            </section>
            <section className='flex flex-col items-center justify-center max-md:[&_p]:mb-0'>
              <p className='text-primary mb-5 flex items-end text-2xl leading-5 font-semibold'>Frente do RG</p>
              <img src='/img/front-rg.jpg' alt='Frente do RG com CPF - exemplo' />
            </section>
          </div>
        </Modal>
        <Modal isOpen={isSelfieModalOpen} handleClose={setIsSelfieModalState.toFalse}>
          <div className='flex flex-col items-center gap-10 text-center [&_img]:max-w-full max-md:[&_svg]:h-fit max-md:[&_svg]:w-full max-md:[&_svg]:max-w-80'>
            <p className='text-primary mb-5 text-2xl font-semibold'>Segure seu documento e grave um vídeo</p>
            <img src='/img/selfie.svg' alt='Vídeo selfie - exemplo' className='-mb-16' />
          </div>
        </Modal>
        <Modal isOpen={isCnhModalOpen} handleClose={setIsCnhModalState.toFalse}>
          <div className='flex flex-col gap-4 [&_img]:max-w-full max-md:[&_svg]:h-fit max-md:[&_svg]:w-full'>
            <p className='text-primary text-2xl font-semibold'>CNH - exemplo</p>
            <img src='/img/cnh.jpg' alt='CNH - exemplo' />
          </div>
        </Modal>
      </Layout>
    </>
  );
}

export { Documents };
